/**
 * The Access page's "Completed" tab, for auditors (6 Oct, decision 34).
 *
 * One row per completed (archived) access case — person, type, case request,
 * requested by, manager, started, finished — and, opened from its Items
 * cell, each item's Ivanti request (linked to the HelpDesk ticket page), its
 * final status and every approval: pick, rule, matrix approver, who decided
 * (name and signed-in email), test approval, on-behalf, decision, UTC time,
 * reason and decision id. Filters: finished dates (12 months by default),
 * type, system, approver or decided-by text. Test approvals follow the
 * server's default (excluded in production, included on test systems, where
 * a small "Test" badge marks them; 28 Oct: no filter or banner for them).
 * "Export to Excel" downloads the same list, one row per approval.
 *
 * Admins and IT only: the tab is shown on `useMe().access_audit`, and the
 * server answers 403 to anyone else.
 */

import { Download, Icon, Search } from "@norican/icons";
import {
  Badge,
  Button,
  DataGrid,
  type DataGridColumn,
  Field,
  Input,
  Select,
  Typography,
} from "@norican/react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import {
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  ResultStateNotice,
  useDateFormatter,
} from "@/components/common";
import { useDebouncedValue } from "@/hooks";
import { useT } from "@/i18n";
import type { AccessAuditApproval, AccessAuditCase, AccessAuditItem } from "@/lib/api";

import { accessCompletedExportUrl, type AccessCompletedQuery, useAccessCompleted } from "../api";
import { accessCasePath, withLocalDates } from "../lib/access";

type TestChoice = "default" | "include" | "exclude";

interface Draft {
  from: string;
  to: string;
  type: "all" | "new" | "change" | "leave";
  system: string;
  approver: string;
  test: TestChoice;
}

const EMPTY: Draft = { from: "", to: "", type: "all", system: "", approver: "", test: "default" };

/** The filter as the query string carries it; blanks are left to the server's defaults. */
export function completedQuery(draft: Draft, approver: string): AccessCompletedQuery {
  const query: AccessCompletedQuery = {};
  if (draft.from !== "") {
    query.finished_from = draft.from;
  }
  if (draft.to !== "") {
    query.finished_to = draft.to;
  }
  if (draft.type !== "all") {
    query.type = draft.type;
  }
  if (draft.system !== "") {
    query.system = draft.system;
  }
  if (approver.trim() !== "") {
    query.approver = approver.trim().slice(0, 200);
  }
  if (draft.test !== "default") {
    query.test = draft.test;
  }
  return query;
}

/** "2026-10-05 14:03:22 UTC": an approval's time as its record states it. */
export function utcText(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return `${parsed.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

const DECISION_TONE: Record<string, "success" | "danger" | "neutral" | "warning"> = {
  approved: "success",
  rejected: "danger",
  pre_approved: "neutral",
  no_approver: "warning",
  waiting: "warning",
};

const DECISIONS = ["approved", "rejected", "pre_approved", "no_approver", "waiting"] as const;

export function AccessCompletedPanel() {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const approver = useDebouncedValue(draft.approver, 400);
  const query = useMemo(() => completedQuery(draft, approver), [draft, approver]);
  const completed = useAccessCompleted(query, true);
  const data = completed.data;
  const applied = data?.filter;
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));

  const typeOptions = [
    { value: "all", label: t("access.completed.allTypes") },
    { value: "new", label: t("access.type.new") },
    { value: "change", label: t("access.type.change") },
    { value: "leave", label: t("access.type.leave") },
  ];
  const systemOptions = [
    { value: "", label: t("access.completed.allSystems") },
    ...(data?.systems ?? []).map((system) => ({ value: system.key, label: system.label })),
  ];

  const columns: DataGridColumn<AccessAuditCase>[] = [
    {
      id: "person",
      header: t("access.completed.col.person"),
      accessor: (row) => row.person_name,
      sortable: true,
      cell: (row) => (
        <span className="flex flex-wrap items-center gap-1">
          <span className="font-medium">{row.person_name}</span>
          {row.test_approvals === true ? (
            <Badge
              tone="neutral"
              variant="subtle"
              size="xs"
              role="img"
              title={t("access.completed.test.badgeHint")}
              aria-label={t("access.completed.test.badgeHint")}
            >
              {t("access.completed.test.badge")}
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      id: "type",
      header: t("access.completed.col.type"),
      accessor: (row) => row.type,
      cell: (row) => (
        <Badge tone="accent" variant="subtle" size="xs">
          {t(`access.type.${row.type}`)}
        </Badge>
      ),
    },
    {
      id: "case",
      header: t("access.completed.col.case"),
      accessor: (row) => row.case_number,
      sortable: true,
      cell: (row) => (
        <Link
          to={accessCasePath(row.case_id)}
          className="text-accent-fg-subtle font-mono text-sm font-semibold underline-offset-2 hover:underline"
        >
          {row.case_number}
        </Link>
      ),
    },
    {
      id: "requested_by",
      header: t("access.completed.col.requestedBy"),
      accessor: (row) => row.requested_by ?? null,
    },
    {
      id: "manager",
      header: t("access.completed.col.manager"),
      accessor: (row) => row.manager ?? null,
    },
    {
      id: "started",
      header: t("access.completed.col.started"),
      accessor: (row) => row.started_at ?? null,
      sortable: true,
      cell: (row) => (row.started_at ? formatDate(row.started_at) : "—"),
    },
    {
      id: "finished",
      header: t("access.completed.col.finished"),
      accessor: (row) => row.finished_at ?? null,
      sortable: true,
      cell: (row) => (row.finished_at ? formatDate(row.finished_at) : "—"),
    },
    {
      id: "items",
      header: t("access.completed.col.items"),
      accessor: (row) => (row.items ?? []).length,
      cell: (row) => (
        <span className="text-accent-fg-subtle cursor-pointer text-sm underline-offset-2 hover:underline">
          {/* 14 Oct: "1 approval", not "1 approvals". */}
          {t("access.completed.itemsCount", {
            items: t("access.completed.itemCount", { count: (row.items ?? []).length }),
            approvals: t("access.completed.approvalCount", {
              count: (row.items ?? []).reduce((n, i) => n + (i.approvals ?? []).length, 0),
            }),
          })}
        </span>
      ),
      renderCellDetail: (row) => <CaseItems item={row} />,
    },
  ];

  const rows = data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Typography as="p" variant="body-sm" tone="muted">
        {t("access.completed.hint")}
      </Typography>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field name="completed-from" label={t("access.completed.from")}>
          <Input
            size="sm"
            type="date"
            value={draft.from || (applied?.finished_from ?? "")}
            onChange={(event) => set({ from: event.target.value })}
          />
        </Field>
        <Field name="completed-to" label={t("access.completed.to")}>
          <Input
            size="sm"
            type="date"
            value={draft.to || (applied?.finished_to ?? "")}
            onChange={(event) => set({ to: event.target.value })}
          />
        </Field>
        <Field name="completed-approver" label={t("access.completed.approver")}>
          <Input
            size="sm"
            type="search"
            value={draft.approver}
            maxLength={200}
            placeholder={t("access.completed.approverPlaceholder")}
            startSlot={<Icon icon={Search} size="sm" />}
            onChange={(event) => set({ approver: event.target.value })}
          />
        </Field>
        <Field name="completed-type" label={t("access.completed.type")}>
          <Dropdown
            label={t("access.completed.type")}
            value={draft.type}
            options={typeOptions}
            onChange={(value) => set({ type: value as Draft["type"] })}
          />
        </Field>
        <Field name="completed-system" label={t("access.completed.system")}>
          <Dropdown
            label={t("access.completed.system")}
            value={draft.system}
            options={systemOptions}
            onChange={(value) => set({ system: value })}
          />
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Typography as="span" variant="body-sm" className="font-medium">
          {t("access.completed.count", { count: rows.length })}
        </Typography>
        <span className="grow" />
        <Button
          size="sm"
          variant="outline"
          render={<a href={accessCompletedExportUrl(query)} download />}
          startIcon={<Icon icon={Download} size="sm" />}
        >
          {t("access.completed.export")}
        </Button>
      </div>
      <ResultStateNotice result={data?.result} />
      {completed.isPending ? (
        <LoadingRows rows={4} />
      ) : completed.isError ? (
        <ErrorState error={completed.error} onRetry={() => void completed.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyStateBlock
          title={t("access.completed.empty.title")}
          description={t("access.completed.empty.description")}
        />
      ) : (
        <DataGrid
          aria-label={t("access.completed.label")}
          columns={columns}
          data={rows}
          getRowId={(row) => row.case_id}
          density="compact"
        />
      )}
    </div>
  );
}

/** A small select for the filter bar. */
function Dropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <Select.Root value={value} items={options} onValueChange={(next) => onChange(String(next))}>
      <Select.Trigger size="sm" aria-label={label} />
      <Select.Portal>
        <Select.Positioner sideOffset={4}>
          <Select.Popup>
            <Select.List>
              {options.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}

/** A case's items, opened beneath its row: each request and every approval. */
function CaseItems({ item }: { item: AccessAuditCase }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  return (
    <div className="flex flex-col gap-3 py-1">
      {item.read_only === true ? (
        <Typography as="p" variant="caption" tone="muted">
          {t("access.completed.readOnly")}
        </Typography>
      ) : null}
      {(item.items ?? []).map((entry) => (
        <ItemBlock key={`${entry.n}-${entry.item}`} entry={entry} formatDate={formatDate} />
      ))}
    </div>
  );
}

function ItemBlock({
  entry,
  formatDate,
}: {
  entry: AccessAuditItem;
  formatDate: (value: string) => string;
}) {
  const { t } = useT("tickets");
  const approvals = entry.approvals ?? [];
  return (
    <section className="border-border flex flex-col gap-2 rounded-md border px-3 py-2">
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Typography as="span" variant="heading-6">
          {entry.system}
        </Typography>
        {entry.action ? (
          <Badge tone="neutral" variant="subtle" size="xs">
            {t(`access.action.${entry.action}`)}
          </Badge>
        ) : null}
        {entry.request_number && entry.url_path ? (
          <Link
            to={entry.url_path}
            className="text-accent-fg-subtle font-mono text-sm underline-offset-2 hover:underline"
          >
            {t("access.request", { number: entry.request_number })}
          </Link>
        ) : null}
        {entry.status ? (
          <Typography as="span" variant="caption" tone="muted">
            {t("access.completed.status", { status: entry.status })}
          </Typography>
        ) : null}
        {entry.finished_at ? (
          <Typography as="span" variant="caption" tone="muted">
            {t("access.completed.finishedAt", { date: formatDate(entry.finished_at) })}
          </Typography>
        ) : null}
      </span>
      {approvals.length === 0 ? (
        <Typography as="p" variant="caption" tone="muted">
          {t("access.completed.noApprovals")}
        </Typography>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {approvals.map((approval, index) => (
            <li key={approval.decision_id ?? `${approval.need_id ?? "r"}-${index}`}>
              <ApprovalLine approval={approval} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ApprovalLine({ approval }: { approval: AccessAuditApproval }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const decision = DECISIONS.find((d) => d === approval.decision);
  const yesNo = (flag: boolean | undefined) =>
    flag === true ? t("access.completed.yes") : t("access.completed.no");
  const lines = [
    approval.pick ? t("access.completed.pick", { pick: approval.pick }) : null,
    approval.rule
      ? t("access.completed.rule", { rule: withLocalDates(approval.rule, formatDate) })
      : null,
    approval.approver ? t("access.completed.matrixApprover", { name: approval.approver }) : null,
    approval.decided_by
      ? t("access.completed.decidedBy", {
          name: approval.decided_by,
          email: approval.decided_by_email ?? "—",
        })
      : null,
    approval.decided_at ? utcText(approval.decided_at) : null,
    approval.source === "matrix"
      ? `${t("access.completed.testApproval", { value: yesNo(approval.test_approval) })} · ${t(
          "access.completed.onBehalf",
          { value: yesNo(approval.on_behalf) },
        )}${approval.on_behalf_of ? ` (${approval.on_behalf_of})` : ""}`
      : t("access.completed.ivantiRecord"),
    approval.reason ? t("access.completed.reason", { reason: approval.reason }) : null,
    approval.decision_id ? t("access.completed.decisionId", { id: approval.decision_id }) : null,
  ].filter((line): line is string => line !== null);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-2">
        <Badge tone={DECISION_TONE[approval.decision] ?? "neutral"} variant="subtle" size="xs">
          {decision ? t(`access.completed.decision.${decision}`) : approval.decision}
        </Badge>
        {approval.test_approval === true ? (
          <Badge tone="neutral" variant="subtle" size="xs">
            {t("access.completed.test.badge")}
          </Badge>
        ) : null}
      </span>
      {lines.map((line) => (
        <Typography
          key={line}
          as="span"
          variant="caption"
          className="min-w-0 break-words"
          tone="muted"
        >
          {line}
        </Typography>
      ))}
    </div>
  );
}
